import { isCatalogMilestonePlanComplete } from "./remanufacturing/catalogMilestones";
import { MILESTONE_KEY } from "./remanufacturing/constants";
import { milestonesFromOrder } from "./remanufacturing/remanufacturingMilestoneAdapter";
import { normalizeServiceEoiDetails } from "./serviceEoiDetails";
import { serviceWorkflowFromInterestMessage } from "./serviceEoiMessage";
import { isServiceListing } from "./types";

/** @typedef {"eoi_received"|"feasibility_declined"|"awaiting_quote"|"quote_sent"|"quote_expired"|"quote_accepted"|"milestones"|"completed"|"cancelled"} ServiceWorkflowPhase */

export const SERVICE_WORKFLOW_PHASE = {
  EOI_RECEIVED: "eoi_received",
  CANCELLED: "cancelled",
  FEASIBILITY_DECLINED: "feasibility_declined",
  AWAITING_QUOTE: "awaiting_quote",
  QUOTE_SENT: "quote_sent",
  QUOTE_EXPIRED: "quote_expired",
  QUOTE_ACCEPTED: "quote_accepted",
  MILESTONES: "milestones",
  COMPLETED: "completed",
};

const LEGACY_MILESTONE_KEYS = new Set([
  MILESTONE_KEY.M1,
  MILESTONE_KEY.M2,
  MILESTONE_KEY.M3,
  MILESTONE_KEY.M4,
]);

const CATALOG_MILESTONE_KEY_RE = /^SVC\d+$/i;

const TERMINAL_ORDER_STATUSES = new Set(["completed", "declined", "cancelled"]);

/** @typedef {import("./serviceEoiDetails").ServiceEoiDetails} ServiceEoiDetails */

/**
 * @typedef {Object} ServiceWorkflowQuote
 * @property {number} [amount]
 * @property {string} [currency]
 * @property {string} [deliveryDate]
 * @property {string} [expiresAt]
 * @property {string} [note]
 * @property {Array<{ assetRef?: string, fileName?: string, mimeType?: string }>} [attachments]
 */

/**
 * @typedef {Object} ServiceWorkflow
 * @property {ServiceWorkflowPhase} [phase]
 * @property {ServiceEoiDetails} [eoiDetails]
 * @property {{ verdict?: "will_quote"|"declined", note?: string, decidedAt?: string }} [feasibility]
 * @property {ServiceWorkflowQuote} [quote]
 * @property {string} [quoteAcceptedAt]
 * @property {string} [poReference]
 * @property {"legacy_m1_m3"|"catalog"} [milestonePlanMode]
 */

/**
 * @param {unknown} raw
 * @returns {ServiceWorkflow|null}
 */
export function normalizeServiceWorkflow(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (raw);
  const feasibilityRaw = record.feasibility;
  const quoteRaw = record.quote;
  const eoiDetailsRaw = record.eoiDetails ?? record.eoi_details;

  /** @type {ServiceWorkflow} */
  const workflow = {
    ...(typeof record.phase === "string" ? { phase: record.phase } : {}),
    ...(typeof record.quoteAcceptedAt === "string"
      ? { quoteAcceptedAt: record.quoteAcceptedAt }
      : typeof record.quote_accepted_at === "string"
        ? { quoteAcceptedAt: record.quote_accepted_at }
        : {}),
    ...(typeof record.poReference === "string"
      ? { poReference: record.poReference }
      : typeof record.po_reference === "string"
        ? { poReference: record.po_reference }
        : {}),
    ...(record.milestonePlanMode === "legacy_m1_m3" ||
    record.milestonePlanMode === "catalog" ||
    record.milestone_plan_mode === "legacy_m1_m3" ||
    record.milestone_plan_mode === "catalog"
      ? {
          milestonePlanMode:
            record.milestonePlanMode ?? record.milestone_plan_mode,
        }
      : {}),
  };

  const normalizedEoi = normalizeServiceEoiDetails(eoiDetailsRaw);
  if (normalizedEoi) {
    workflow.eoiDetails = normalizedEoi;
  }

  if (
    feasibilityRaw &&
    typeof feasibilityRaw === "object" &&
    !Array.isArray(feasibilityRaw)
  ) {
    const feasibility = /** @type {Record<string, unknown>} */ (feasibilityRaw);
    workflow.feasibility = {
      ...(feasibility.verdict === "will_quote" || feasibility.verdict === "declined"
        ? { verdict: feasibility.verdict }
        : {}),
      ...(typeof feasibility.note === "string" ? { note: feasibility.note } : {}),
      ...(typeof feasibility.decidedAt === "string"
        ? { decidedAt: feasibility.decidedAt }
        : typeof feasibility.decided_at === "string"
          ? { decidedAt: feasibility.decided_at }
          : {}),
    };
  }

  if (quoteRaw && typeof quoteRaw === "object" && !Array.isArray(quoteRaw)) {
    const quote = /** @type {Record<string, unknown>} */ (quoteRaw);
    workflow.quote = {
      ...(typeof quote.amount === "number" ? { amount: quote.amount } : {}),
      ...(typeof quote.currency === "string" ? { currency: quote.currency } : {}),
      ...(typeof quote.deliveryDate === "string"
        ? { deliveryDate: quote.deliveryDate }
        : typeof quote.delivery_date === "string"
          ? { deliveryDate: quote.delivery_date }
          : {}),
      ...(typeof quote.expiresAt === "string"
        ? { expiresAt: quote.expiresAt }
        : typeof quote.expires_at === "string"
          ? { expiresAt: quote.expires_at }
          : {}),
      ...(typeof quote.note === "string" ? { note: quote.note } : {}),
      ...(Array.isArray(quote.attachments)
        ? { attachments: quote.attachments }
        : {}),
    };
  }

  return workflow;
}

/**
 * Same merge as {@link normalizeMarketplaceOrder}: persisted `serviceWorkflow`, else JSON `message`.
 *
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {ServiceWorkflow|null}
 */
export function resolveServiceWorkflowFromOrder(order) {
  const serviceWorkflowRaw =
    order?.serviceWorkflow ?? order?.service_workflow ?? null;
  const message = typeof order?.message === "string" ? order.message : null;

  return (
    normalizeServiceWorkflow(serviceWorkflowRaw) ??
    serviceWorkflowFromInterestMessage(message)
  );
}

/**
 * EOI details from `serviceWorkflow` or parsed commercial interest `message`.
 * Merges both sources so dimensions survive partial API persistence (workflow without
 * `partConstraints` while `message` still holds the full v2 envelope).
 *
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {ServiceEoiDetails|null}
 */
export function resolveOrderEoiDetails(order) {
  const messageWorkflow = serviceWorkflowFromInterestMessage(
    typeof order?.message === "string" ? order.message : null
  );
  const persistedWorkflow = normalizeServiceWorkflow(
    order?.serviceWorkflow ?? order?.service_workflow
  );

  const workflowEoi = persistedWorkflow?.eoiDetails;
  const messageEoi = messageWorkflow?.eoiDetails;

  if (!workflowEoi && !messageEoi) {
    return null;
  }
  if (!workflowEoi) {
    return messageEoi ?? null;
  }
  if (!messageEoi) {
    return workflowEoi;
  }

  return normalizeServiceEoiDetails({
    ...messageEoi,
    ...workflowEoi,
    customerContact: {
      ...messageEoi.customerContact,
      ...workflowEoi.customerContact,
    },
    machine: {
      ...messageEoi.machine,
      ...workflowEoi.machine,
    },
    repairScope: {
      ...messageEoi.repairScope,
      ...workflowEoi.repairScope,
    },
    partConstraints: {
      ...messageEoi.partConstraints,
      ...workflowEoi.partConstraints,
    },
    requirements: {
      ...messageEoi.requirements,
      ...workflowEoi.requirements,
    },
    attachments:
      workflowEoi.attachments?.length > 0
        ? workflowEoi.attachments
        : messageEoi.attachments,
  });
}

/**
 * @param {ServiceWorkflowQuote|undefined|null} quote
 */
export function isServiceQuoteExpired(quote) {
  if (!quote?.expiresAt) return false;
  const expiresAt = Date.parse(quote.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt < Date.now();
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {Array<Record<string, unknown>>}
 */
function getOrderMilestoneRows(order) {
  const serviceMilestones = order?.serviceMilestones;
  if (Array.isArray(serviceMilestones)) {
    return serviceMilestones;
  }
  if (
    serviceMilestones &&
    typeof serviceMilestones === "object" &&
    Array.isArray(
      /** @type {{ milestones?: unknown[] }} */ (serviceMilestones).milestones
    )
  ) {
    return /** @type {{ milestones: Record<string, unknown>[] }} */ (
      serviceMilestones
    ).milestones;
  }
  return [];
}

/**
 * @param {Record<string, unknown>} milestone
 */
function isCatalogMilestoneRow(milestone) {
  const key = String(milestone?.key ?? "");
  if (CATALOG_MILESTONE_KEY_RE.test(key)) {
    return true;
  }
  const inputs = milestone?.inputs;
  return Boolean(
    inputs &&
      typeof inputs === "object" &&
      !Array.isArray(inputs) &&
      Array.isArray(/** @type {{ activities?: unknown }} */ (inputs).activities)
  );
}

/**
 * @param {Record<string, unknown>} milestone
 */
function isLegacyMilestoneRow(milestone) {
  return LEGACY_MILESTONE_KEYS.has(String(milestone?.key ?? ""));
}

/**
 * Catalog-driven milestone plans use SVC* keys or per-milestone activity selections.
 * @param {Record<string, unknown>|null|undefined} order
 */
export function usesCatalogMilestones(order) {
  if (!isServiceListing(order)) return false;

  const workflow = normalizeServiceWorkflow(order?.serviceWorkflow);
  if (workflow?.milestonePlanMode === "catalog") {
    return true;
  }
  if (workflow?.milestonePlanMode === "legacy_m1_m3") {
    return false;
  }

  const milestones = getOrderMilestoneRows(order);
  if (milestones.some(isCatalogMilestoneRow)) {
    return true;
  }

  if (
    workflow?.quoteAcceptedAt &&
    !milestones.some(isLegacyMilestoneRow)
  ) {
    return true;
  }

  return false;
}

/**
 * Existing M1–M3 (and M4 follow-up) provider workflow.
 * @param {Record<string, unknown>|null|undefined} order
 */
export function usesLegacyRemanufacturingMilestones(order) {
  if (!isServiceListing(order)) return false;

  const workflow = normalizeServiceWorkflow(order?.serviceWorkflow);
  if (workflow?.milestonePlanMode === "legacy_m1_m3") {
    return true;
  }
  if (workflow?.milestonePlanMode === "catalog") {
    return false;
  }

  const milestones = getOrderMilestoneRows(order);
  if (milestones.length === 0) {
    return !(
      workflow?.quoteAcceptedAt || workflow?.milestonePlanMode === "catalog"
    );
  }

  if (milestones.some(isCatalogMilestoneRow)) {
    return false;
  }

  return milestones.some(isLegacyMilestoneRow);
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function shouldUseLegacyRemanufacturingProviderWorkflow(order) {
  if (!isServiceListing(order)) return false;

  const workflow = normalizeServiceWorkflow(order?.serviceWorkflow);
  if (
    workflow?.milestonePlanMode === "catalog" ||
    workflow?.quoteAcceptedAt
  ) {
    return false;
  }

  return usesLegacyRemanufacturingMilestones(order) && !usesCatalogMilestones(order);
}

/**
 * @param {ServiceWorkflowPhase} phase
 */
export function isServiceWorkflowMilestonePhase(phase) {
  return (
    phase === SERVICE_WORKFLOW_PHASE.MILESTONES ||
    phase === SERVICE_WORKFLOW_PHASE.COMPLETED
  );
}

/**
 * @param {ServiceWorkflowPhase} phase
 */
export function isServiceWorkflowCommercialPhase(phase) {
  return (
    phase === SERVICE_WORKFLOW_PHASE.AWAITING_QUOTE ||
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_SENT ||
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_EXPIRED ||
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_ACCEPTED
  );
}

/**
 * Resolves the active workflow phase for a remanufacturing service order.
 * Legacy orders without `serviceWorkflow` infer M1–M3 milestone execution when active.
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {ServiceWorkflowPhase|null}
 */
export function resolveServiceWorkflowPhase(order) {
  if (!isServiceListing(order)) {
    return null;
  }

  const workflow =
    normalizeServiceWorkflow(order?.serviceWorkflow) ??
    serviceWorkflowFromInterestMessage(order?.message);
  const status = String(order?.status ?? "");
  const milestones = getOrderMilestoneRows(order);

  if (status === "cancelled") {
    return SERVICE_WORKFLOW_PHASE.CANCELLED;
  }

  if (workflow?.phase) {
    if (
      workflow.phase === SERVICE_WORKFLOW_PHASE.QUOTE_SENT &&
      isServiceQuoteExpired(workflow.quote)
    ) {
      return SERVICE_WORKFLOW_PHASE.QUOTE_EXPIRED;
    }
    return workflow.phase;
  }

  if (status === "completed") {
    return SERVICE_WORKFLOW_PHASE.COMPLETED;
  }

  if (workflow?.feasibility?.verdict === "declined" || status === "declined") {
    return SERVICE_WORKFLOW_PHASE.FEASIBILITY_DECLINED;
  }

  if (workflow?.quoteAcceptedAt) {
    return SERVICE_WORKFLOW_PHASE.MILESTONES;
  }

  if (workflow?.quote) {
    return isServiceQuoteExpired(workflow.quote)
      ? SERVICE_WORKFLOW_PHASE.QUOTE_EXPIRED
      : SERVICE_WORKFLOW_PHASE.QUOTE_SENT;
  }

  if (workflow?.feasibility?.verdict === "will_quote") {
    return SERVICE_WORKFLOW_PHASE.AWAITING_QUOTE;
  }

  if (status === "active") {
    if (usesCatalogMilestones(order) && milestones.length === 0) {
      return SERVICE_WORKFLOW_PHASE.QUOTE_ACCEPTED;
    }
    return SERVICE_WORKFLOW_PHASE.MILESTONES;
  }

  if (
    order?.source === "interest" &&
    status === "pending_provider" &&
    !TERMINAL_ORDER_STATUSES.has(status)
  ) {
    return SERVICE_WORKFLOW_PHASE.EOI_RECEIVED;
  }

  if (workflow?.eoiDetails) {
    return SERVICE_WORKFLOW_PHASE.EOI_RECEIVED;
  }

  return SERVICE_WORKFLOW_PHASE.EOI_RECEIVED;
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canProviderSubmitFeasibility(order) {
  if (String(order?.status ?? "") === "cancelled") {
    return false;
  }
  return resolveServiceWorkflowPhase(order) === SERVICE_WORKFLOW_PHASE.EOI_RECEIVED;
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canProviderSubmitQuote(order) {
  if (String(order?.status ?? "") === "cancelled") {
    return false;
  }
  const phase = resolveServiceWorkflowPhase(order);
  return (
    phase === SERVICE_WORKFLOW_PHASE.AWAITING_QUOTE ||
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_EXPIRED
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canProviderViewSentServiceQuote(order) {
  const phase = resolveServiceWorkflowPhase(order);
  const quote = normalizeServiceWorkflow(order?.serviceWorkflow)?.quote;
  if (!quote) return false;

  return (
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_SENT ||
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_ACCEPTED
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canCustomerAcceptServiceQuote(order) {
  return resolveServiceWorkflowPhase(order) === SERVICE_WORKFLOW_PHASE.QUOTE_SENT;
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canCustomerViewAcceptedServiceQuote(order) {
  if (!usesCommercialServiceWorkflow(order)) return false;

  const workflow = normalizeServiceWorkflow(order?.serviceWorkflow);
  return Boolean(workflow?.quote && workflow.quoteAcceptedAt);
}

/**
 * @typedef {"provider_milestone_plan"|"milestone_execution"|"completed"} CustomerAcceptedQuotePendingStep
 */

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {CustomerAcceptedQuotePendingStep}
 */
export function resolveCustomerAcceptedQuotePendingStep(order) {
  const phase = resolveServiceWorkflowPhase(order);
  if (phase === SERVICE_WORKFLOW_PHASE.COMPLETED) {
    return "completed";
  }

  const milestones = getOrderMilestoneRows(order);
  if (milestones.length === 0) {
    return "provider_milestone_plan";
  }

  const allCompleted = milestones.every(
    (milestone) => String(milestone?.status ?? "") === "completed"
  );
  return allCompleted ? "completed" : "milestone_execution";
}

/**
 * Customer-facing delivery pack (completed catalog service) instead of the
 * provider milestone editor / raw progress list.
 *
 * @param {Record<string, unknown>|null|undefined} order
 */
export function shouldShowCustomerServiceDeliveryPack(order) {
  if (!order || !usesCatalogMilestones(order)) {
    return false;
  }

  if (resolveCustomerAcceptedQuotePendingStep(order) === "completed") {
    return true;
  }

  if (String(order.status ?? "") === "completed") {
    return true;
  }

  const milestones = milestonesFromOrder(order);
  return isCatalogMilestonePlanComplete(milestones);
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function canProviderBuildCatalogMilestones(order) {
  const phase = resolveServiceWorkflowPhase(order);
  return (
    phase === SERVICE_WORKFLOW_PHASE.QUOTE_ACCEPTED ||
    (phase === SERVICE_WORKFLOW_PHASE.MILESTONES && usesCatalogMilestones(order))
  );
}

const COMMERCIAL_WORKFLOW_PHASES = new Set([
  SERVICE_WORKFLOW_PHASE.EOI_RECEIVED,
  SERVICE_WORKFLOW_PHASE.FEASIBILITY_DECLINED,
  SERVICE_WORKFLOW_PHASE.AWAITING_QUOTE,
  SERVICE_WORKFLOW_PHASE.QUOTE_SENT,
  SERVICE_WORKFLOW_PHASE.QUOTE_EXPIRED,
  SERVICE_WORKFLOW_PHASE.QUOTE_ACCEPTED,
]);

/**
 * Commercial TMCOMAS flow (EOI → feasibility → quote → acceptance).
 * Legacy service interests with only a free-text message keep provider Accept/Decline.
 * @param {Record<string, unknown>|null|undefined} order
 */
export function usesCommercialServiceWorkflow(order) {
  if (!isServiceListing(order)) return false;

  const workflow =
    normalizeServiceWorkflow(order?.serviceWorkflow) ??
    serviceWorkflowFromInterestMessage(order?.message);

  if (
    workflow?.eoiDetails &&
    Object.keys(workflow.eoiDetails).length > 0
  ) {
    return true;
  }

  return Boolean(
    workflow?.phase && COMMERCIAL_WORKFLOW_PHASES.has(workflow.phase)
  );
}

/**
 * @param {ServiceWorkflowPhase|null|undefined} phase
 * @param {(key: string) => string} t
 */
export function getServiceWorkflowPhaseLabel(phase, t) {
  if (!phase) return "";
  const key = `serviceWorkflowPhase_${phase}`;
  const label = t(key);
  return label === key ? phase.replaceAll("_", " ") : label;
}
